import * as React from 'react';

import { ReactFCC } from '@notino/react-styleguide';

import { useTrackingContext } from '@context/tracking/TrackingContext';
import { dispatchTrackingEvent } from '@context/tracking/utils';
import { registerKeyboardListeners } from '@helpers/accessibility/registerKeyboardListeners';
import { useTabSwitcher } from '@sharedComponents/TabSwitcher/context/TabSwitcherContext';
import { useTabLink } from '@sharedComponents/TabSwitcher/useTabLink';

import { useNewPdDesignEnabled } from '../../hooks/useNewPdDesignEnabled';

import { LinkWrapper } from './styled';

interface IContentTabLinkProps {
  tabId: number;
  isDefaultTab?: boolean;
  id: string;
  trackingLabel?: string;
  ariaControls?: string;
}

export const ContentTabLink: ReactFCC<IContentTabLinkProps> = ({
  tabId,
  isDefaultTab = false,
  trackingLabel,
  id,
  children,
  ariaControls,
}) => {
  const ref = React.useRef<HTMLButtonElement>();
  const { getTimeFromInit } = useTrackingContext();
  const { isActive, setActiveTabHandler } = useTabLink(tabId, isDefaultTab);
  const { register, selectNext, selectPrevious } = useTabSwitcher();
  const newDesign = useNewPdDesignEnabled();

  React.useEffect(() => {
    register(tabId, ref);
  }, [register, tabId]);

  const handleClick = () => {
    setActiveTabHandler();
    if (trackingLabel) {
      dispatchTrackingEvent({
        event: 'subpage_view',
        subpage: {
          name: trackingLabel,
          type: 'tab',
          interaction: 'click',
          timing: getTimeFromInit(),
          action: 'click_on_tab',
        },
        _clear: true,
      });
    }
  };

  return (
    <LinkWrapper
      id={id}
      ref={ref}
      role="tab"
      aria-controls={ariaControls}
      aria-selected={isActive}
      tabIndex={isActive ? 0 : -1}
      isActive={isActive}
      newDesign={newDesign}
      onClick={handleClick}
      {...registerKeyboardListeners({
        ArrowRight: selectNext,
        ArrowLeft: selectPrevious,
      })}
    >
      {children}
    </LinkWrapper>
  );
};
